import type { components } from '../api/schema.gen';
import type { RedeemState } from './enums';
const REDEEM_STATE_COLOR: Record<RedeemState, string> = {
  10: 'default',
  20: 'processing',
  40: 'success',
  60: 'error',
  80: 'warning',
  85: 'processing',
  86: 'warning',
};
const REDEEM_STATE_LABEL_KEY: Record<RedeemState, string> = {
  10: 'redeems.states.10',
  20: 'redeems.states.20',
  40: 'redeems.states.40',
  60: 'redeems.states.60',
  80: 'redeems.states.80',
  85: 'redeems.states.85',
  86: 'redeems.states.86',
};
export function redeemStateColor(state: RedeemState | null | undefined): string {
  return state === null || state === undefined ? 'default' : REDEEM_STATE_COLOR[state];
}
export function redeemStateLabelKey(state: RedeemState | null | undefined): string {
  return state === null || state === undefined
    ? 'redeems.states.unknown'
    : REDEEM_STATE_LABEL_KEY[state];
}
export function isRedeemTerminal(state: RedeemState): boolean {
  return state === 40 || state === 60 || state === 80;
}
/**
 * Steam 支持的全部币种（47 种）。
 *
 * 顺序：前三位是 **用户裁定的常用序**（USD / EUR / CNY，2026-10-20），
 * 其余按 Steam 官方 ecode 序（权威来源：`backend/src/steam/protocol/money.py`
 * 的 `ISO_BY_ECODE`，ecode 2..47）。
 *
 * 集合内容必须与后端 `ISO_BY_ECODE` 一致 —— 前端下拉里的单位就是后端
 * 能认的单位，少一个用户就选不到，多一个后端解析不了。
 */
export const AMOUNT_UNITS = [
  'USD',
  'EUR',
  'CNY',
  'GBP',
  'CHF',
  'RUB',
  'PLN',
  'BRL',
  'JPY',
  'NOK',
  'IDR',
  'MYR',
  'PHP',
  'SGD',
  'THB',
  'VND',
  'KRW',
  'TRY',
  'UAH',
  'MXN',
  'CAD',
  'AUD',
  'NZD',
  'INR',
  'CLP',
  'PEN',
  'COP',
  'ZAR',
  'HKD',
  'TWD',
  'SAR',
  'AED',
  'SEK',
  'ARS',
  'ILS',
  'BYN',
  'KZT',
  'KWD',
  'QAR',
  'CRC',
  'UYU',
  'BGN',
  'HRK',
  'CZK',
  'DKK',
  'HUF',
  'RON',
] as const;
export type AmountUnit = (typeof AMOUNT_UNITS)[number];
export const DEFAULT_AMOUNT_UNIT: AmountUnit = 'USD';
/** 取词条用的翻译函数（`useTranslation()` 的 `t`）。 */
export type AmountTranslator = (key: string) => string;
/**
 * 每个币种在 `common.amountUnits.*` 下的词条键。
 *
 * 键必须写成**字面量** —— i18n 闸扫的是源码里的字面量字符串，
 * 拼出来的键它扫不到（扫不到 = 没有闸 = 缺键会静默印出原始键）。
 */
export const AMOUNT_UNIT_LABEL_KEYS: Record<AmountUnit, string> = {
  USD: 'common.amountUnits.USD',
  EUR: 'common.amountUnits.EUR',
  CNY: 'common.amountUnits.CNY',
  GBP: 'common.amountUnits.GBP',
  CHF: 'common.amountUnits.CHF',
  RUB: 'common.amountUnits.RUB',
  PLN: 'common.amountUnits.PLN',
  BRL: 'common.amountUnits.BRL',
  JPY: 'common.amountUnits.JPY',
  NOK: 'common.amountUnits.NOK',
  IDR: 'common.amountUnits.IDR',
  MYR: 'common.amountUnits.MYR',
  PHP: 'common.amountUnits.PHP',
  SGD: 'common.amountUnits.SGD',
  THB: 'common.amountUnits.THB',
  VND: 'common.amountUnits.VND',
  KRW: 'common.amountUnits.KRW',
  TRY: 'common.amountUnits.TRY',
  UAH: 'common.amountUnits.UAH',
  MXN: 'common.amountUnits.MXN',
  CAD: 'common.amountUnits.CAD',
  AUD: 'common.amountUnits.AUD',
  NZD: 'common.amountUnits.NZD',
  INR: 'common.amountUnits.INR',
  CLP: 'common.amountUnits.CLP',
  PEN: 'common.amountUnits.PEN',
  COP: 'common.amountUnits.COP',
  ZAR: 'common.amountUnits.ZAR',
  HKD: 'common.amountUnits.HKD',
  TWD: 'common.amountUnits.TWD',
  SAR: 'common.amountUnits.SAR',
  AED: 'common.amountUnits.AED',
  SEK: 'common.amountUnits.SEK',
  ARS: 'common.amountUnits.ARS',
  ILS: 'common.amountUnits.ILS',
  BYN: 'common.amountUnits.BYN',
  KZT: 'common.amountUnits.KZT',
  KWD: 'common.amountUnits.KWD',
  QAR: 'common.amountUnits.QAR',
  CRC: 'common.amountUnits.CRC',
  UYU: 'common.amountUnits.UYU',
  BGN: 'common.amountUnits.BGN',
  HRK: 'common.amountUnits.HRK',
  CZK: 'common.amountUnits.CZK',
  DKK: 'common.amountUnits.DKK',
  HUF: 'common.amountUnits.HUF',
  RON: 'common.amountUnits.RON',
};
/** 「名称(代码)」展示。调用方传 `useTranslation()` 的 `t`。 */
export function amountUnitLabel(unit: AmountUnit, t: AmountTranslator): string {
  return `${t(AMOUNT_UNIT_LABEL_KEYS[unit])}(${unit})`;
}
/** 按当前语言给「名称(代码)」的选项。调用方传 `useTranslation()` 的 `t`。 */
export function amountUnitOptions(t: AmountTranslator): { value: AmountUnit; label: string }[] {
  return AMOUNT_UNITS.map((unit) => ({ value: unit, label: amountUnitLabel(unit, t) }));
}
/**
 * 金额三元组。后端给 `text`（可缺省），前端展示时自己补。
 *
 * `value` 是**字符串**不是数字 —— 金额不做浮点运算（7×0.10 必须精确等于 0.70）。
 */
export interface AmountTriple {
  /** spec 里是 `anyOf[string, null]` —— 显式收 null，别用 `?: string`。 */
  text?: string | null;
  value: string;
  unit: string;
}
/** wire 上的金额：三栏全可空，且三栏都不是 `required`。 */
export type WireAmount = components['schemas']['Amount-Output'];
/**
 * wire 金额数组 → `AmountTags` 的入参（= `AmountTriple[]`）。
 *
 * 入参**显式收 null / undefined**：wire 上金额字段真的可空。逐栏判空而不是
 * 直接透传，否则 `null` 会被拼成 `null USD` 显示出去。三栏全空的那一条不渲染
 * —— 没有可显示内容的 Tag 是噪音；`value` 有值而 `unit` 缺失仍要显示。
 */
export function amountTagsOf(
  amounts: readonly WireAmount[] | null | undefined,
): readonly AmountTriple[] {
  if (amounts === null || amounts === undefined) return [];
  return amounts.flatMap((amount) => {
    const value = amount.value ?? '';
    const text = amount.text ?? '';
    if (value === '' && text === '') return [];
    return [{ value, unit: amount.unit ?? '', ...(text === '' ? {} : { text }) }];
  });
}
/** 展示用：优先后端给的 `text`，缺省时自己拼 `value + unit`。 */
export function amountText(amount: AmountTriple | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  if (amount.text !== undefined && amount.text !== null && amount.text !== '') return amount.text;
  return `${amount.value} ${amount.unit}`;
}
/**
 * 金额数值规范化到两位小数。
 *
 * 放这里而不是解析器里：「批量文本解析」与「编辑弹窗」都要它，
 * 两处各写一份迟早会漂（一边 `toFixed(2)` 一边原样传）。
 *
 * 非数字**原样返回** —— 不吞：用户填了 `abc` 时要能看见填错字，
 * 而不是悄悄变成 `NaN` 或者 `0.00`。
 *
 * ⚠️ **显示层用，不是账**：走 `Number` ⇒ IEEE-754 的四舍五入（`0.005` ⇒
 * `0.01`，而十进制的 `ROUND_HALF_EVEN` 会是 `0.00`）⇒ 边界上差一位。
 * ⇒ 金额的**真值**在后端（`Decimal`），这里的数只给人看；参与账的计算一律
 * 用后端那个。
 */
export function toAmountValue(raw: string): string {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : raw;
}
/**
 * 同币种金额求和。
 *
 * ⚠️ 走「分」而不是直接相加浮点：`0.1 + 0.2 !== 0.3` 在金额上会显示成
 * `0.30` 却对不上账 —— 所以先转分再转回。
 *
 * 何时用：一张表里同一币种出现多次时（多次充值、多次扣款），合并成一个
 * 总额展示，而不是列出 N 个同币种 Tag。
 */
export function sumAmountTriples(groups: readonly (readonly AmountTriple[])[]): AmountTriple[] {
  const byUnit = new Map<string, number>();
  for (const triples of groups) {
    for (const item of triples) {
      const cents = toCents(item.value);
      if (cents === null) continue;
      byUnit.set(item.unit, (byUnit.get(item.unit) ?? 0) + cents);
    }
  }
  return [...byUnit].map(([unit, cents]) => ({ value: (cents / 100).toFixed(2), unit }));
}
function toCents(value: string): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}
